import React, { useEffect, useRef, useState } from "react";
import { Reorder } from "framer-motion";
import { api, coverUrl, uploadFileChunked } from "@/lib/api";
import { toast } from "sonner";
import { X, Save, Upload } from "lucide-react";
import { PackTrackRow } from "@/components/PackTrackRow";
import { PackBulkPanel } from "@/components/PackBulkPanel";
import { TagsInput, tagsToForm } from "@/components/TagsInput";
import { PriceToggle } from "@/components/PriceToggle";
import { HighlightSelect, sectionsToForm } from "@/components/HighlightSelect";
import { splitGenres } from "@/components/GenrePill";

const inputCls = "w-full bg-[#16161D] border border-white/10 rounded-lg px-3 py-2 text-white text-sm";
const fileCls = "w-full text-xs text-white/60 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-red-600/20 file:text-red-400 file:font-semibold hover:file:bg-red-600/30";

export function EditTrackModal({ track, onClose, onSaved }) {
  const [f, setF] = useState({
    title: track.title, artist: track.artist, category: track.category, genre: track.genre || "",
    bpm: track.bpm || "", tags: track.tags || [], price: String(track.price ?? 0), is_featured: !!track.is_featured, is_top: !!track.is_top, is_highlighted: !!track.is_highlighted, is_new: !!track.is_new, highlight_genres: track.highlight_genres || [], preview_start: track.preview_start || 0, sections: track.sections || [],
  });
  const [audio, setAudio] = useState(null);
  const [cover, setCover] = useState(null);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [versions, setVersions] = useState(track.versions || []);
  const [addingV, setAddingV] = useState(false);

  const addVersions = async (files) => {
    if (!files.length) return;
    setAddingV(true);
    const fd = new FormData();
    files.forEach((file) => fd.append("versions", file));
    try {
      const res = await api.post(`/admin/tracks/${track.id}/versions`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setVersions(res.data.versions || []);
      toast.success("Versión añadida");
      onSaved();
    } catch { toast.error("Error al añadir versión"); } finally { setAddingV(false); }
  };
  const renameVersion = async (vid, title) => {
    const res = await api.put(`/admin/tracks/${track.id}/versions/${vid}`, { title });
    setVersions(res.data.versions || []);
    onSaved();
  };
  const artistVersion = async (vid, artist) => {
    const res = await api.put(`/admin/tracks/${track.id}/versions/${vid}`, { artist });
    setVersions(res.data.versions || []);
    onSaved();
  };
  const priceVersion = async (vid, price) => {
    const res = await api.put(`/admin/tracks/${track.id}/versions/${vid}`, { price: Number(price) || 0 });
    setVersions(res.data.versions || []);
    onSaved();
  };
  const removeVersion = async (vid) => {
    const res = await api.delete(`/admin/tracks/${track.id}/versions/${vid}`);
    setVersions(res.data.versions || []);
    onSaved();
  };

  const reset = async () => {
    setF({ ...f, genre: "", bpm: "", tags: [], is_featured: false, is_top: false, is_highlighted: false, is_new: false, highlight_genres: [], sections: [], preview_start: 0, price: "0" });
    if (!track.pack_id) return;
    if (!window.confirm("Este tema viene de un pack. ¿Quitarlo también de la sección? Seguirá disponible dentro del pack.")) return;
    try {
      await api.delete(`/admin/tracks/${track.id}/unpublish`);
      toast.success("Tema quitado de la sección");
      onSaved();
      onClose();
    } catch { toast.error("No se pudo quitar de la sección"); }
  };

  const [savedAt, setSavedAt] = useState(null);
  const dirtyRef = useRef(false);
  const firstRef = useRef(true);

  const save = async ({ close = false, silent = true } = {}) => {
    if (!f.title || !f.artist) { if (!silent) toast.error("Título y artista son obligatorios"); return; }
    setSaving(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, k === "tags" ? tagsToForm(v) : (k === "sections" || k === "highlight_genres") ? sectionsToForm(v) : v));
    if (audio) fd.append("audio", audio);
    if (cover) fd.append("cover", cover);
    try {
      await api.put(`/admin/tracks/${track.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => ev.total && setProgress(Math.round((ev.loaded / ev.total) * 100)),
      });
      dirtyRef.current = false;
      setSavedAt(new Date());
      if (audio) setAudio(null);
      if (cover) setCover(null);
      onSaved();
      if (close) onClose();
    } catch (err) {
      toast.error("Error al guardar");
      console.error(err);
    } finally { setSaving(false); }
  };

  useEffect(() => {
    if (firstRef.current) { firstRef.current = false; return; }
    dirtyRef.current = true;
    const t = setTimeout(() => save(), 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f, audio, cover]);

  const submit = (e) => { e.preventDefault(); save({ silent: false }); };

  return (
    <ModalShell title="Editar Track" onClose={onClose} testid="edit-track-modal">
      <form onSubmit={submit} className="space-y-3">
        <div className="flex gap-4 items-start">
          <div className="w-24 h-24 rounded-lg overflow-hidden bg-neutral-900 shrink-0 border border-white/5">
            {(cover || track.cover_path) && (
              <img src={cover ? URL.createObjectURL(cover) : coverUrl(track.cover_path)} alt="" className="w-full h-full object-cover" />
            )}
          </div>
          <div className="flex-1 space-y-3">
            <input className={inputCls} placeholder="Título" value={f.title} data-testid="edit-track-title"
              onChange={(e) => setF({ ...f, title: e.target.value })} />
            <input className={inputCls} placeholder="Artista" value={f.artist} data-testid="edit-track-artist"
              onChange={(e) => setF({ ...f, artist: e.target.value })} />
          </div>
        </div>
        <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={inputCls} data-testid="edit-track-category">
          <option value="remix">Remix</option>
          <option value="mashup">Mashup</option>
          <option value="original">Original</option>
          <option value="pool">Pool</option>
        </select>
        <input className={inputCls} placeholder="Género(s), separados por coma" value={f.genre} onChange={(e) => setF({ ...f, genre: e.target.value })} data-testid="edit-track-genre" />
        <input className={inputCls} type="number" placeholder="BPM" value={f.bpm} onChange={(e) => setF({ ...f, bpm: e.target.value })} data-testid="edit-track-bpm" />
        <div>
          <span className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1 block">Precio</span>
          <PriceToggle value={f.price} onChange={(price) => setF({ ...f, price })} testid="edit-track-price" />
        </div>
        <TagsInput value={f.tags} onChange={(tags) => setF({ ...f, tags })} testid="edit-track-tag" />
        <HighlightSelect value={{ is_featured: f.is_featured, is_top: f.is_top, is_highlighted: f.is_highlighted, is_new: f.is_new, highlight_genres: f.highlight_genres, sections: f.sections }} genres={splitGenres(f.genre)} category={f.category} onChange={(v) => setF({ ...f, ...v })} testid="edit-track-highlight" />
        <FileRow label="Reemplazar audio (opcional)" accept="audio/*" file={audio} onChange={setAudio} testid="edit-track-audio" />
        <FileRow label="Reemplazar portada (opcional)" accept="image/*" file={cover} onChange={setCover} testid="edit-track-cover" />

        <div className="border border-white/10 rounded-xl p-3 space-y-2" data-testid="edit-track-versions">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">Versiones ({versions.length})</span>
          <DropLabel busy={addingV} label="+ Añadir versión" busyLabel="Subiendo..." testid="edit-track-add-versions" onFiles={addVersions} />
          </div>
          {versions.length === 0 && <div className="text-xs text-white/30 py-1">Sin versiones extra (p. ej. Extended, Radio Edit, Instrumental).</div>}
          {versions.map((v) => (
            <div key={v.id} className="space-y-1" data-testid={`edit-track-version-${v.id}`}>
              <div className="flex items-center gap-2">
              <input defaultValue={v.title} key={v.title} data-testid={`edit-track-version-title-${v.id}`}
                onBlur={(e) => e.target.value.trim() && e.target.value.trim() !== v.title && renameVersion(v.id, e.target.value.trim())}
                className="flex-1 bg-[#16161D] border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs" />
              <input defaultValue={v.artist || ""} key={`a-${v.artist || ""}`} placeholder="Artista" data-testid={`edit-track-version-artist-${v.id}`}
                onBlur={(e) => e.target.value.trim() !== (v.artist || "") && artistVersion(v.id, e.target.value.trim())}
                className="w-28 bg-[#16161D] border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs" />
              <span className="font-mono text-[10px] text-white/40 w-10 text-right">{v.duration ? `${Math.floor(v.duration / 60)}:${String(Math.round(v.duration % 60)).padStart(2, "0")}` : ""}</span>
              <button type="button" onClick={() => removeVersion(v.id)} data-testid={`edit-track-remove-version-${v.id}`}
                className="text-white/40 hover:text-red-500 p-1 text-xs">✕</button>
              </div>
              <div className="pl-1 pb-1" data-testid={`edit-track-version-price-row-${v.id}`}>
                <PriceToggle small value={String(v.price ?? f.price ?? 0)} onChange={(price) => priceVersion(v.id, price)} testid={`edit-track-version-price-${v.id}`} />
              </div>
            </div>
          ))}
        </div>

        <button type="button" data-testid="edit-track-reset"
          onClick={reset}
          className="w-full text-xs text-white/50 hover:text-red-400 py-1">
          Restablecer etiquetas y opciones (género, BPM, tags, destacado, top, precio{track.pack_id ? " · quitar de la sección" : ""})
        </button>
        <AutoSaveStatus saving={saving} savedAt={savedAt} progress={progress} hasFile={!!audio || !!cover} testid="edit-track-save-status" />
      </form>
    </ModalShell>
  );
}

export function EditPackModal({ pack, onClose, onSaved }) {
  const [f, setF] = useState({
    title: pack.title, description: pack.description || "",
    price: String(pack.price ?? 0), is_featured: !!pack.is_featured, is_new: !!pack.is_new, coming_soon: !!pack.coming_soon,
  });
  const [tracks, setTracks] = useState(pack.tracks || []);
  const [cover, setCover] = useState(null);
  const [zip, setZip] = useState(null);
  const [zipProgress, setZipProgress] = useState(0);
  const [removeZip, setRemoveZip] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [addingTracks, setAddingTracks] = useState(false);
  const [pistaGenre, setPistaGenre] = useState("");
  const pgq = pistaGenre.trim().toLowerCase();
  const [pistaSearch, setPistaSearch] = useState("");
  const psq = pistaSearch.trim().toLowerCase();
  const visiblePistas = tracks.filter((t) => (!pgq || (pgq === "-" ? !(t.genre || "").trim() : (t.genre || "").toLowerCase().includes(pgq))) && (!psq || `${t.title} ${t.artist || ""}`.toLowerCase().includes(psq)));
  const pistaGenreList = [...new Set(tracks.flatMap((t) => (t.genre || "").split(",").map((g) => g.trim()).filter(Boolean)))].sort();
  const orderDirty = useRef(false);
  const tracksRef = useRef(tracks);
  const [selected, setSelected] = useState([]);
  const allSelected = tracks.length > 0 && selected.length === tracks.length;
  const toggleAll = () => setSelected(allSelected ? [] : tracks.map((t) => t.id));
  const toggleOne = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  useEffect(() => { tracksRef.current = tracks; }, [tracks]);

  const moveTrack = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= tracksRef.current.length) return;
    const next = [...tracksRef.current];
    const [item] = next.splice(i, 1);
    next.splice(j, 0, item);
    setTracks(next); tracksRef.current = next; orderDirty.current = true;
    persistOrder();
  };

  const persistOrder = async () => {
    if (!orderDirty.current) return;
    orderDirty.current = false;
    try {
      const res = await api.put(`/admin/packs/${pack.id}/tracks/reorder`, { ids: tracksRef.current.map((t) => t.id) });
      setTracks(res.data.tracks || []);
      toast.success("Orden de pistas guardado");
      onSaved();
    } catch {
      toast.error("No se pudo guardar el orden");
    }
  };

  const addTracks = async (files) => {
    if (!files.length) return;
    setAddingTracks(true);
    const fd = new FormData();
    files.forEach((file) => fd.append("tracks", file));
    try {
      const res = await api.post(`/admin/packs/${pack.id}/tracks`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTracks(res.data.tracks || []);
      toast.success(`${files.length} pista${files.length === 1 ? "" : "s"} añadida${files.length === 1 ? "" : "s"}`);
      onSaved();
    } catch {
      toast.error("Error al añadir pistas");
    } finally { setAddingTracks(false); }
  };

  const removeTrack = async (tid) => {
    try {
      const res = await api.delete(`/admin/packs/${pack.id}/tracks/${tid}`);
      setTracks(res.data.tracks || []);
      onSaved();
    } catch {
      toast.error("No se pudo quitar la pista");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title) return toast.error("El título es obligatorio");
    setSaving(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    if (cover) fd.append("cover", cover);
    if (removeZip && !zip) fd.append("remove_archive", "true");
    try {
      if (zip) { setZipProgress(0); const path = await uploadFileChunked(zip, { folder: "packs", contentType: "application/zip", onProgress: setZipProgress }); fd.append("archive_path", path); }
      await api.put(`/admin/packs/${pack.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => ev.total && setProgress(Math.round((ev.loaded / ev.total) * 100)),
      });
      toast.success("Pack actualizado");
      onSaved();
      onClose();
    } catch (err) {
      toast.error("Error al guardar");
      console.error(err);
    } finally { setSaving(false); }
  };

  return (
    <ModalShell title="Editar Pack" onClose={onClose} testid="edit-pack-modal" wide>
      <form onSubmit={submit} className="space-y-3">
        <div className="flex gap-4 items-start">
          <div className="w-28 h-28 rounded-lg overflow-hidden bg-neutral-900 shrink-0 border border-white/5" data-testid="edit-pack-cover-preview">
            {(cover || pack.cover_path) && (
              <img src={cover ? URL.createObjectURL(cover) : coverUrl(pack.cover_path)} alt="" className="w-full h-full object-cover" />
            )}
          </div>
          <div className="flex-1 space-y-3">
            <input className={inputCls} placeholder="Título" value={f.title} data-testid="edit-pack-title"
              onChange={(e) => setF({ ...f, title: e.target.value })} />
            <textarea className={inputCls} rows={3} placeholder="Descripción" value={f.description} data-testid="edit-pack-description"
              onChange={(e) => setF({ ...f, description: e.target.value })} />
          </div>
        </div>
        <div>
          <span className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1 block">Precio</span>
          <PriceToggle value={f.price} onChange={(price) => setF({ ...f, price })} testid="edit-pack-price" defaultPaid="24.99" />
        </div>
        <label className="flex items-center gap-2 text-sm text-white/70">
          <input type="checkbox" checked={f.is_featured} onChange={(e) => setF({ ...f, is_featured: e.target.checked })} data-testid="edit-pack-featured" />
          Destacar en Home
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="flex items-center gap-2 text-xs text-white/70 rounded-lg border border-white/10 px-3 py-2 cursor-pointer">
            <input type="checkbox" checked={f.is_new} onChange={(e) => setF({ ...f, is_new: e.target.checked })} data-testid="edit-pack-new" className="accent-red-600" /> Novedad (Inicio)
          </label>
          <label className="flex items-center gap-2 text-xs text-white/70 rounded-lg border border-white/10 px-3 py-2 cursor-pointer" title="El pack completo no se puede comprar ni descargar; solo las pistas sueltas">
            <input type="checkbox" checked={f.coming_soon} onChange={(e) => setF({ ...f, coming_soon: e.target.checked })} data-testid="edit-pack-coming-soon" className="accent-red-600" /> Próximamente
          </label>
        </div>
        <FileRow label="Reemplazar portada (opcional)" accept="image/*" file={cover} onChange={setCover} testid="edit-pack-cover" />
        <div className="rounded-xl border border-white/10 p-3 space-y-2" data-testid="edit-pack-zip">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">ZIP del pack</span>
            <span className={`text-[11px] ${pack.archive_path && !removeZip ? "text-emerald-400" : "text-white/40"}`} data-testid="edit-pack-zip-status">
              {zip ? `Nuevo: ${zip.name} (${(zip.size / 1024 / 1024).toFixed(1)} MB)${saving && zipProgress > 0 ? ` · subiendo ${zipProgress}%` : ""}` : pack.archive_path && !removeZip ? "ZIP manual subido" : "Se genera automáticamente con las pistas"}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-[11px] px-3 py-1.5 rounded-full border border-dashed border-red-800/50 text-red-400 hover:bg-red-600/10 cursor-pointer" data-testid="edit-pack-zip-upload">
              {pack.archive_path ? "Reemplazar ZIP" : "Subir ZIP manual"}
              <input type="file" accept=".zip,application/zip" className="hidden" data-testid="edit-pack-zip-input" onChange={(e) => { setZip(e.target.files[0] || null); setRemoveZip(false); }} />
            </label>
            {zip && <button type="button" onClick={() => setZip(null)} data-testid="edit-pack-zip-cancel" className="text-[11px] text-white/50 hover:text-white">Cancelar</button>}
            {pack.archive_path && !zip && (
              <button type="button" onClick={() => setRemoveZip((v) => !v)} data-testid="edit-pack-zip-remove" className={`text-[11px] px-3 py-1.5 rounded-full border ${removeZip ? "border-red-600 text-white bg-red-600/30" : "border-white/10 text-white/60 hover:text-white"}`}>
                {removeZip ? "Se quitará al guardar · deshacer" : "Quitar ZIP manual (volver al automático)"}
              </button>
            )}
          </div>
        </div>

        <div className="border border-white/10 rounded-xl p-3 space-y-2" data-testid="edit-pack-tracks">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-white/50 cursor-pointer">
              <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="edit-pack-select-all" />
              Pistas MP3 ({tracks.length}){selected.length > 0 && <span className="text-red-400"> · {selected.length} sel.</span>}
            </label>
            <DropLabel busy={addingTracks} label="+ Añadir pistas" busyLabel="Subiendo..." testid="edit-pack-add-tracks" onFiles={addTracks} />
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="edit-pack-genre-filter-row">
            <div className="relative flex items-center">
              <input value={pistaSearch} onChange={(e) => setPistaSearch(e.target.value)} placeholder="Buscar pista…" data-testid="edit-pack-search"
                className={`bg-[#16161D] border rounded-full pl-3 pr-7 py-1.5 text-xs text-white outline-none w-48 ${psq ? "border-red-600/60" : "border-white/10 focus:border-red-600/60"}`} />
              {psq && <button type="button" onClick={() => setPistaSearch("")} data-testid="edit-pack-search-clear" className="absolute right-2 text-white/40 hover:text-white text-xs">✕</button>}
            </div>
            <div className="relative flex items-center">
              <input list="edit-pack-genre-options" value={pistaGenre} onChange={(e) => setPistaGenre(e.target.value)} placeholder="Filtrar pistas por género… (- = sin género)" data-testid="edit-pack-genre-filter"
                className={`bg-[#16161D] border rounded-full pl-3 pr-7 py-1.5 font-mono text-[10px] uppercase tracking-widest text-white outline-none w-64 ${pgq ? "border-red-600/60" : "border-white/10 focus:border-red-600/60"}`} />
              <datalist id="edit-pack-genre-options">{pistaGenreList.map((g) => <option key={g} value={g} />)}</datalist>
              {pgq && <button type="button" onClick={() => setPistaGenre("")} data-testid="edit-pack-genre-filter-clear" className="absolute right-2 text-white/40 hover:text-white text-xs">✕</button>}
            </div>
            {(pgq || psq) && <span className="font-mono text-[10px] uppercase tracking-widest text-red-400" data-testid="edit-pack-genre-filter-count">{visiblePistas.length} de {tracks.length}</span>}
            {(pgq || psq) && <button type="button" onClick={() => setSelected(visiblePistas.map((t) => t.id))} data-testid="edit-pack-select-filtered" className="text-[11px] text-white/60 hover:text-red-400">Seleccionar las {visiblePistas.length} mostradas</button>}
          </div>
          {selected.length > 0 && (
            <PackBulkPanel pack={pack} selected={selected} tracks={tracks} onClear={() => setSelected([])}
              onApplied={(updated) => { setTracks(updated.tracks || []); onSaved(); }} />
          )}
          {tracks.length === 0 && <div className="text-xs text-white/30 py-2">Sin pistas. Añade MP3 para que el pack se pueda escuchar y descargar.</div>}
          {tracks.length > 0 && visiblePistas.length === 0 && <div className="text-xs text-white/30 py-2" data-testid="edit-pack-genre-filter-empty">Ninguna pista coincide con "{(pistaSearch || pistaGenre).trim()}".</div>}
          <Reorder.Group axis="y" values={visiblePistas} onReorder={(next) => { if (pgq || psq) return; setTracks(next); orderDirty.current = true; }} className="space-y-2">
            {visiblePistas.map((t) => {
              const i = tracks.indexOf(t);
              return (
              <PackTrackRow key={t.id} pack={pack} track={t} index={i} total={tracks.length}
                onMove={(dir) => moveTrack(i, dir)}
                selected={selected.includes(t.id)} onToggle={() => toggleOne(t.id)}
                onUpdated={(updated) => { setTracks(updated.tracks || []); onSaved(); }}
                onRemove={removeTrack} onDragEnd={persistOrder} />
              );
            })}
          </Reorder.Group>
        </div>
        <SaveBar saving={saving} progress={progress} hasFile={!!cover} testid="edit-pack-save" />
      </form>
    </ModalShell>
  );
}

function DropLabel({ busy, label, busyLabel, testid, onFiles }) {
  const [over, setOver] = useState(false);
  const take = (list) => { const files = Array.from(list || []).filter((f) => f.type.startsWith("audio/") || /\.(mp3|wav|flac|aiff?|m4a|ogg)$/i.test(f.name)); if (files.length) onFiles(files); else toast.error("Suelta archivos de audio"); };
  return (
    <label data-testid={`${testid}-dropzone`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (!busy) take(e.dataTransfer.files); }}
      className={`text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer border border-dashed transition-colors ${busy ? "opacity-50 border-transparent" : over ? "border-red-500 bg-red-950/30 text-white" : "border-transparent bg-red-600/20 text-red-400 hover:bg-red-600/30"}`}>
      {busy ? busyLabel : over ? "Suelta aquí" : `${label} · o arrastra`}
      <input type="file" multiple accept="audio/*" className="hidden" disabled={busy} data-testid={testid}
        onChange={(e) => { take(e.target.files); e.target.value = ""; }} />
    </label>
  );
}

function ModalShell({ title, onClose, children, testid, wide }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose} data-testid={testid}>
      <div className={`w-full ${wide ? "max-w-4xl" : "max-w-lg"} bg-[#111116] border border-white/10 rounded-2xl p-6 max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-black text-xl uppercase tracking-tight">{title}</h3>
          <button type="button" onClick={onClose} data-testid={`${testid}-close`} className="text-white/50 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FileRow({ label, accept, file, onChange, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1 block">{label}</span>
      <input type="file" accept={accept} onChange={(e) => onChange(e.target.files[0] || null)} data-testid={testid} className={fileCls} />
      {file && <span className="text-xs text-emerald-400 mt-1 block">{file.name}</span>}
    </label>
  );
}

function AutoSaveStatus({ saving, savedAt, progress, hasFile, testid }) {
  return (
    <div className="pt-1 space-y-1.5" data-testid={testid}>
      <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} data-testid="edit-track-enter-submit" />
      {saving && hasFile && (
        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-red-600 transition-[width]" style={{ width: `${progress}%` }} />
        </div>
      )}
      <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-white/40">
        <span>{saving ? (hasFile ? `Subiendo ${progress}%` : "Guardando…") : savedAt ? `Guardado ✓ ${savedAt.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}` : "Los cambios se guardan automáticamente"}</span>
        <span>Enter = guardar</span>
      </div>
    </div>
  );
}

function SaveBar({ saving, progress, hasFile, testid }) {
  return (
    <div className="space-y-2 pt-1">
      {saving && hasFile && (
        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-red-600 transition-[width]" style={{ width: `${progress}%` }} />
        </div>
      )}
      <button type="submit" disabled={saving} data-testid={testid}
        className="w-full btn-red text-white py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
        {saving ? <Upload className="w-4 h-4 animate-pulse" /> : <Save className="w-4 h-4" />}
        {saving ? (hasFile ? `Subiendo ${progress}%` : "Guardando...") : "Guardar cambios"}
      </button>
    </div>
  );
}
